/**
 * « Ce que le client peut déclarer » — le réglage du formulaire, dossier par
 * dossier.
 *
 * OÙ IL VIT, ET POURQUOI LÀ. Décision de Rose du 16/08/2026 : un dossier se
 * règle UNE FOIS, on n'y revient pas. Un bouton permanent dans le bandeau
 * coûterait donc de la place tous les jours pour un geste annuel. Il est dans
 * le menu « ⋯ », avec les autres actions rares — reprendre depuis OpenPaye,
 * gérer les accès, archiver.
 *
 * CE QU'IL MONTRE, DANS CET ORDRE :
 *
 * 1. CE QUI EST FIXE, en lecture seule et en premier. Les heures
 *    supplémentaires et complémentaires sont proposées à tous les clients,
 *    partout, sans réglage : leur majoration est fixée par la loi. Les montrer
 *    évite la question « pourquoi je ne peux pas les ajouter ? » et évite
 *    surtout qu'on les recrée en double.
 *
 * 2. CE QUI SE RÈGLE. Les primes du dossier, et les heures conventionnelles —
 *    nuit, dimanche, férié — qui dépendent de la convention et n'ont donc rien
 *    à faire dans une liste commune.
 *
 * LE CODE DE RUBRIQUE EST OBLIGATOIRE. Sans lui, la colonne n'est qu'un texte
 * qu'une collaboratrice devra retranscrire : on aurait déplacé le travail, pas
 * supprimé. C'est le seul champ qu'on refuse de laisser vide.
 *
 * L'ORDRE SE RÈGLE, et il compte : c'est celui du formulaire du client. Ce
 * qu'il déclare tous les mois doit être en premier — sur un téléphone, la
 * dernière colonne demande un défilement de plus.
 *
 * ON NE SUPPRIME PAS, ON RETIRE. Une colonne citée par les saisies d'un mois
 * déjà traité doit rester lisible. « Retirer » la sort du formulaire du client
 * et la range en bas, d'où elle peut revenir.
 */

import { useEffect, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  Check,
  Info,
  Lock,
  Pencil,
  Plus,
  RotateCcw,
  X,
} from 'lucide-react';

export interface ColonneReglee {
  uuid: string;
  code: string;
  libelle: string;
  unite: 'heures' | 'montant' | 'nombre' | string;
  valeurUnitaire: number | null;
  aide: string | null;
  rang: number;
  active: boolean;
}

const UNITES = [
  { cle: 'montant', libelle: 'Un montant en €' },
  { cle: 'nombre', libelle: 'Un nombre' },
  { cle: 'heures', libelle: 'Des heures' },
] as const;

/**
 * Ce que le client voit sans qu'on ait rien réglé.
 *
 * Recopié de `NATURES` (features/saisie-client) : si l'une bouge, l'autre doit
 * suivre. C'est court et stable — la loi ne change pas tous les ans — et une
 * dépendance croisée entre l'écran du cabinet et le formulaire public coûterait
 * plus cher que cette recopie.
 */
const FIXES = [
  { libelle: 'Heures sup. à 25 %', detail: 'de la 36e à la 43e heure' },
  { libelle: 'Heures sup. à 50 %', detail: 'à partir de la 44e' },
  { libelle: 'Heures complémentaires', detail: 'temps partiel seulement' },
];

const SUFFIXE: Record<string, string> = {
  heures: 'h',
  montant: '€',
  nombre: '',
};

export function TiroirColonnesSaisie({
  ouvert,
  nomDossier,
  colonnes,
  enregistrement,
  actionFinale,
  onFermer,
  onEnregistrer,
  onChangerEtat,
  onReordonner,
}: {
  ouvert: boolean;
  nomDossier: string;
  colonnes: ColonneReglee[];
  enregistrement: boolean;
  /**
   * Le geste qui a amené ici, quand il y en a un : « Envoyer le lien au
   * client ». Fermer le tiroir (croix, Échap, clic sur le fond) RENONCE — un
   * mail ne part jamais d'un geste d'abandon (verdict QA du 22/09/2026, M1).
   */
  actionFinale?: { libelle: string; onClick: () => void };
  onFermer: () => void;
  onEnregistrer: (c: Partial<ColonneReglee>) => Promise<void>;
  onChangerEtat: (uuid: string, active: boolean) => Promise<void>;
  onReordonner: (uuids: string[]) => Promise<void>;
}) {
  /** `null` = rien d'ouvert, `''` = création, sinon l'uuid modifié. */
  const [edition, setEdition] = useState<string | null>(null);

  useEffect(() => {
    if (!ouvert) setEdition(null);
  }, [ouvert]);

  useEffect(() => {
    if (!ouvert) return;
    const surEchap = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Échap ferme d'abord le formulaire, puis le tiroir : sinon on perd sa
      // saisie en voulant seulement annuler une ligne.
      if (edition !== null) setEdition(null);
      else onFermer();
    };
    document.addEventListener('keydown', surEchap);
    return () => document.removeEventListener('keydown', surEchap);
  }, [ouvert, edition, onFermer]);

  if (!ouvert) return null;

  const enService = colonnes.filter(c => c.active);
  const retirees = colonnes.filter(c => !c.active);

  /**
   * Monter ou descendre une colonne.
   *
   * Deux flèches plutôt qu'un glisser-déposer : le geste marche au clavier,
   * marche au doigt, et ne demande aucune précision. Sur trois ou quatre
   * colonnes — le cas courant — il est aussi rapide.
   *
   * On n'envoie que les colonnes EN SERVICE : les retirées gardent leur place
   * derrière, le serveur les repousse à la fin.
   */
  const deplacer = (index: number, sens: -1 | 1) => {
    const ordre = [...enService];
    const cible = index + sens;
    if (cible < 0 || cible >= ordre.length) return;
    [ordre[index], ordre[cible]] = [ordre[cible], ordre[index]];
    void onReordonner(ordre.map(c => c.uuid));
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        aria-label="Fermer"
        onClick={onFermer}
        className="flex-1 bg-neutral-900/20"
      />
      <div className="flex h-full w-full max-w-[560px] flex-col bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-[var(--color-primary-100)] px-5 py-3.5">
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold text-neutral-900">
              Ce que le client peut déclarer
            </h2>
            <p className="mt-0.5 truncate text-[12px] text-neutral-500">
              {nomDossier} — se règle une fois, s’applique à tous les mois
            </p>
          </div>
          <button
            type="button"
            onClick={onFermer}
            aria-label="Fermer"
            className="rounded-lg p-1 text-neutral-400 hover:bg-[var(--color-neutral-50)] hover:text-neutral-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* 1 — le fixe, d'abord, pour qu'on ne cherche pas à le recréer. */}
          <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium tracking-wide text-neutral-500 uppercase">
            <Lock className="h-3 w-3" />
            Toujours proposé
          </p>
          <div className="mb-1 rounded-xl border border-[var(--color-primary-100)] bg-[var(--color-neutral-50)] px-3.5 py-2.5">
            {FIXES.map(f => (
              <div
                key={f.libelle}
                className="flex items-baseline gap-2 py-0.5 text-[12.5px]"
              >
                <span className="text-neutral-700">{f.libelle}</span>
                <span className="text-[11.5px] text-neutral-400">
                  {f.detail}
                </span>
              </div>
            ))}
          </div>
          <p className="mb-5 flex items-start gap-1.5 text-[11.5px] leading-relaxed text-neutral-500">
            <Info className="mt-0.5 h-3 w-3 shrink-0" />
            Leur majoration est fixée par la loi : elles sont les mêmes sur tous
            les dossiers et ne se règlent pas. La nuit, le dimanche et les jours
            fériés, eux, dépendent de la convention — ils s’ajoutent ci-dessous.
          </p>

          {/* 2 — le réglable. */}
          <div className="mb-1.5 flex items-center justify-between">
            <p className="text-[11px] font-medium tracking-wide text-neutral-500 uppercase">
              Propre à ce dossier
            </p>
            {edition === null && (
              <button
                type="button"
                onClick={() => setEdition('')}
                className="flex items-center gap-1.5 rounded-lg border border-[var(--color-primary-100)] bg-white px-2.5 py-1 text-[12px] text-[var(--color-primary-600)] hover:border-[var(--color-primary-400)] hover:shadow-sm"
              >
                <Plus className="h-3.5 w-3.5" />
                Ajouter
              </button>
            )}
          </div>

          {edition === '' && (
            <Formulaire
              enregistrement={enregistrement}
              onValider={async c => {
                await onEnregistrer(c);
                setEdition(null);
              }}
              onAnnuler={() => setEdition(null)}
            />
          )}

          {enService.length === 0 && edition !== '' && (
            <div className="rounded-xl border border-dashed border-[var(--color-primary-100)] px-4 py-5 text-center">
              <p className="text-[13px] text-neutral-600">
                Aucune colonne réglée pour ce dossier.
              </p>
              <p className="mt-1 text-[12px] text-neutral-500">
                Le client ne verra que les heures supplémentaires et un champ de
                commentaire.
              </p>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            {enService.map((c, i) =>
              edition === c.uuid ? (
                <Formulaire
                  key={c.uuid}
                  colonne={c}
                  enregistrement={enregistrement}
                  onValider={async modif => {
                    await onEnregistrer({ ...modif, uuid: c.uuid });
                    setEdition(null);
                  }}
                  onAnnuler={() => setEdition(null)}
                />
              ) : (
                <Ligne
                  key={c.uuid}
                  colonne={c}
                  premiere={i === 0}
                  derniere={i === enService.length - 1}
                  onMonter={() => deplacer(i, -1)}
                  onDescendre={() => deplacer(i, 1)}
                  onModifier={() => setEdition(c.uuid)}
                  onRetirer={() => void onChangerEtat(c.uuid, false)}
                />
              )
            )}
          </div>

          {/* 3 — ce qui a été retiré, en bas, discret. Une colonne retirée
              reste citée par les saisies des mois passés : la faire
              disparaître donnerait à croire qu'elle n'a jamais existé. */}
          {retirees.length > 0 && (
            <>
              <p className="mt-6 mb-1.5 text-[11px] font-medium tracking-wide text-neutral-400 uppercase">
                Retiré du formulaire
              </p>
              <div className="flex flex-col gap-1">
                {retirees.map(c => (
                  <div
                    key={c.uuid}
                    className="group flex items-center gap-2 rounded-lg border border-[var(--color-primary-100)] px-3 py-1.5"
                  >
                    <span className="min-w-0 flex-1 truncate text-[12.5px] text-neutral-400">
                      {c.libelle}
                      <span className="ml-1.5 text-[11px]">{c.code}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => void onChangerEtat(c.uuid, true)}
                      title="Remettre dans le formulaire"
                      className="flex items-center gap-1 text-[11.5px] text-neutral-400 opacity-0 transition-opacity group-hover:opacity-100 hover:text-[var(--color-primary-600)] focus:opacity-100 max-md:opacity-100"
                    >
                      <RotateCcw className="h-3 w-3" />
                      Remettre
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {actionFinale && (
          <div className="flex items-center justify-end gap-3 border-t border-[var(--color-primary-100)] px-5 py-3">
            <button
              type="button"
              onClick={onFermer}
              className="text-[13.5px] text-neutral-500 underline underline-offset-2 hover:text-neutral-700"
            >
              Plus tard
            </button>
            <button
              type="button"
              onClick={actionFinale.onClick}
              disabled={enregistrement}
              className="bg-primary-600 hover:bg-primary-700 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {actionFinale.libelle}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Une colonne au repos.
 *
 * Modifier et retirer n'apparaissent qu'au survol : ce sont des gestes rares,
 * et deux boutons sur chaque ligne feraient une grille de boutons où l'on ne
 * lit plus les libellés.
 */
function Ligne({
  colonne,
  premiere,
  derniere,
  onMonter,
  onDescendre,
  onModifier,
  onRetirer,
}: {
  colonne: ColonneReglee;
  premiere: boolean;
  derniere: boolean;
  onMonter: () => void;
  onDescendre: () => void;
  onModifier: () => void;
  onRetirer: () => void;
}) {
  const detail = [
    colonne.unite === 'heures'
      ? 'en heures'
      : colonne.unite === 'nombre'
        ? 'un nombre'
        : 'un montant',
    colonne.valeurUnitaire !== null
      ? `${colonne.valeurUnitaire.toLocaleString('fr-FR')} € l’unité`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="group flex items-center gap-2.5 rounded-xl border border-[var(--color-primary-100)] bg-white px-3.5 py-2 hover:border-[var(--color-primary-400)]">
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] text-neutral-900">
          {colonne.libelle}
        </p>
        <p className="truncate text-[11.5px] text-neutral-400">
          Rubrique {colonne.code} · {detail}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100 max-md:opacity-100">
        {/* Une flèche qui ne mène nulle part est un bouton mort : la première
            ligne n'a pas de « monter », la dernière pas de « descendre ». La
            place est gardée pour que les autres boutons ne dansent pas d'une
            ligne à l'autre. */}
        <span className="flex items-center">
          {premiere ? (
            <span className="h-[26px] w-[26px]" />
          ) : (
            <button
              type="button"
              onClick={onMonter}
              aria-label={`Monter ${colonne.libelle}`}
              title="Monter"
              className="rounded-lg p-1.5 text-neutral-500 hover:bg-[var(--color-primary-100)] hover:text-[var(--color-primary-600)]"
            >
              <ArrowUp className="h-3.5 w-3.5" />
            </button>
          )}
          {derniere ? (
            <span className="h-[26px] w-[26px]" />
          ) : (
            <button
              type="button"
              onClick={onDescendre}
              aria-label={`Descendre ${colonne.libelle}`}
              title="Descendre"
              className="rounded-lg p-1.5 text-neutral-500 hover:bg-[var(--color-primary-100)] hover:text-[var(--color-primary-600)]"
            >
              <ArrowDown className="h-3.5 w-3.5" />
            </button>
          )}
        </span>
        <button
          type="button"
          onClick={onModifier}
          aria-label={`Modifier ${colonne.libelle}`}
          title="Modifier"
          className="rounded-lg p-1.5 text-neutral-500 hover:bg-[var(--color-primary-100)] hover:text-[var(--color-primary-600)]"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onRetirer}
          aria-label={`Retirer ${colonne.libelle}`}
          title="Retirer du formulaire"
          className="rounded-lg p-1.5 text-neutral-500 hover:bg-red-50 hover:text-red-700"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

/** Le formulaire d'une colonne — n'existe que quand on ajoute ou modifie. */
function Formulaire({
  colonne,
  enregistrement,
  onValider,
  onAnnuler,
}: {
  colonne?: ColonneReglee;
  enregistrement: boolean;
  onValider: (c: Partial<ColonneReglee>) => Promise<void>;
  onAnnuler: () => void;
}) {
  const [libelle, setLibelle] = useState(colonne?.libelle ?? '');
  const [code, setCode] = useState(colonne?.code ?? '');
  const [unite, setUnite] = useState<string>(colonne?.unite ?? 'montant');
  const [valeur, setValeur] = useState(
    colonne?.valeurUnitaire !== null && colonne?.valeurUnitaire !== undefined
      ? String(colonne.valeurUnitaire)
      : ''
  );
  const [aide, setAide] = useState(colonne?.aide ?? '');
  const [erreur, setErreur] = useState<string | null>(null);

  const valider = async () => {
    if (libelle.trim().length < 2) {
      setErreur('Donnez le nom que verra le client.');
      return;
    }
    if (!code.trim()) {
      setErreur(
        'Le code de rubrique OpenPaye est obligatoire : sans lui, la valeur devra être ressaisie à la main.'
      );
      return;
    }
    setErreur(null);
    try {
      await onValider({
        libelle: libelle.trim(),
        code: code.trim().toUpperCase(),
        unite,
        valeurUnitaire: valeur === '' ? null : Number(valeur),
        aide: aide.trim() || null,
      });
    } catch (e) {
      // Le message de l'API (français, utile) plutôt que « Request failed with
      // status code 403 » (verdict QA du 22/09/2026, M3).
      const reponse = (
        e as { response?: { data?: { message?: string | string[] } } }
      )?.response?.data?.message;
      const message = Array.isArray(reponse) ? reponse[0] : reponse;
      setErreur(message || 'L’enregistrement a échoué.');
    }
  };

  return (
    <div className="mb-1.5 rounded-xl border border-[var(--color-primary-400)] bg-white p-4">
      <div className="mb-3 grid grid-cols-3 gap-2.5">
        <div className="col-span-2">
          <label
            htmlFor="colonne-libelle"
            className="mb-1 block text-[12px] text-neutral-600"
          >
            Nom vu par le client
          </label>
          <input
            id="colonne-libelle"
            value={libelle}
            onChange={e => setLibelle(e.target.value)}
            placeholder="Panier repas, heures de nuit…"
            className="w-full rounded-lg border border-[var(--color-primary-100)] px-3 py-2 text-[13.5px] placeholder:text-neutral-400/50"
          />
        </div>
        <div>
          <label
            htmlFor="colonne-code"
            className="mb-1 block text-[12px] text-neutral-600"
          >
            Rubrique OpenPaye
          </label>
          <input
            id="colonne-code"
            value={code}
            onChange={e => setCode(e.target.value.toUpperCase())}
            placeholder="P12"
            className="w-full rounded-lg border border-[var(--color-primary-100)] px-3 py-2 text-[13.5px] uppercase placeholder:text-neutral-400/50"
          />
        </div>
      </div>

      <p className="mb-1.5 text-[12px] text-neutral-600">
        Le client y saisira…
      </p>
      <div className="mb-3 flex flex-wrap gap-1.5">
        {UNITES.map(u => (
          <button
            key={u.cle}
            type="button"
            onClick={() => setUnite(u.cle)}
            className={`rounded-full border px-3 py-1.5 text-[12.5px] transition-colors ${
              unite === u.cle
                ? 'border-[var(--color-primary-600)] bg-[var(--color-primary-100)] text-[var(--color-primary-600)]'
                : 'border-[var(--color-primary-100)] text-neutral-700 hover:border-[var(--color-primary-400)]'
            }`}
          >
            {u.libelle}
          </button>
        ))}
      </div>

      <div className="mb-3 grid grid-cols-3 gap-2.5">
        <div>
          <label
            htmlFor="colonne-valeur"
            className="mb-1 block text-[12px] text-neutral-600"
          >
            Valeur unitaire
            <span className="ml-1 text-neutral-400">— si fixe</span>
          </label>
          <input
            id="colonne-valeur"
            type="number"
            step="0.01"
            value={valeur}
            onChange={e => setValeur(e.target.value)}
            placeholder="8,50"
            className="w-full rounded-lg border border-[var(--color-primary-100)] px-3 py-2 text-right text-[13.5px] tabular-nums placeholder:text-neutral-400/50"
          />
        </div>
        <div className="col-span-2">
          <label
            htmlFor="colonne-aide"
            className="mb-1 block text-[12px] text-neutral-600"
          >
            Précision pour le client
            <span className="ml-1 text-neutral-400">— facultatif</span>
          </label>
          <input
            id="colonne-aide"
            value={aide}
            onChange={e => setAide(e.target.value)}
            placeholder="Nombre de jours travaillés sur site"
            className="w-full rounded-lg border border-[var(--color-primary-100)] px-3 py-2 text-[13.5px] placeholder:text-neutral-400/50"
          />
        </div>
      </div>

      {/* Ce que ça donnera chez le client : la colonne telle qu'il la verra.
          Régler un formulaire à l'aveugle est le meilleur moyen d'y laisser
          une faute qu'on ne verra qu'au retour du client. */}
      {libelle.trim() && (
        <div className="mb-3 rounded-lg bg-[var(--color-neutral-50)] px-3 py-2">
          <p className="text-[11px] tracking-wide text-neutral-400 uppercase">
            Chez le client
          </p>
          <p className="mt-0.5 text-[12.5px] text-neutral-700">
            {libelle.trim().toUpperCase()}
            {valeur !== '' && (
              <span className="text-neutral-400">
                {' '}
                · {Number(valeur).toLocaleString('fr-FR')} €
                {unite === 'nombre' ? ' l’unité' : ''}
              </span>
            )}
            <span className="text-neutral-400">
              {' '}
              · saisie en {SUFFIXE[unite] || 'nombre'}
            </span>
          </p>
        </div>
      )}

      {erreur && (
        <p className="mb-2.5 rounded-lg bg-red-50 px-3 py-2 text-[12.5px] text-red-800">
          {erreur}
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          disabled={enregistrement}
          onClick={() => void valider()}
          className="flex items-center gap-1.5 rounded-xl bg-[var(--color-primary-600)] px-4 py-2 text-[13.5px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          <Check className="h-3.5 w-3.5" />
          {enregistrement ? 'Enregistrement…' : 'Enregistrer'}
        </button>
        <button
          type="button"
          onClick={onAnnuler}
          className="rounded-xl border border-[var(--color-primary-100)] px-4 py-2 text-[13.5px] text-neutral-600 hover:border-[var(--color-primary-400)]"
        >
          Annuler
        </button>
      </div>
    </div>
  );
}
